import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../../../middleware/api/apiList'
import invoicesApi from '../../../../../middleware/api/ProjectManagement/Projects/ProjectView/Invoices/invoicesApi'
import { AppDispatch, RootState } from '../../../../../stateStore'
import { LoadingState, ValidationError } from '../../../../../types/commonTypes'
import {
  invoicesListSlice,
  InvoicesOfMilestone,
  InvoicesOfMilestoneList,
  InvoicesList,
  InvoiceSummary,
  latestInvoiceVersion,
  invoiceTracker,
} from '../../../../../types/ProjectManagement/Project/ProjectView/Invoices/invoicesTypes'

const getClosedMilestonesAndCRs = createAsyncThunk<
  InvoicesList,
  number | string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectView/getClosedMilestonesAndCRs',
  async (projectId: number | string, thunkApi) => {
    try {
      return await invoicesApi.getClosedMilestonesAndCRs(projectId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoicesOfMilestone = createAsyncThunk<
  InvoicesOfMilestoneList,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'projectView/getInvoicesOfMilestone',
  async (milestoneId: number, thunkApi) => {
    try {
      return await invoicesApi.getInvoicesOfMilestone(milestoneId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getInvoiceSummary = createAsyncThunk<
  InvoiceSummary,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/getInvoiceSummary', async (invoiceId: number, thunkApi) => {
  try {
    return await invoicesApi.getInvoiceSummary(invoiceId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getLatestInvoiceVersionSummary = createAsyncThunk<
  latestInvoiceVersion,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('projectView/latestInvoiceVersion', async (invoiceId: number, thunkApi) => {
  try {
    return await invoicesApi.getlatestInvoiceSummary(invoiceId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
const isProformaRefrece = createAsyncThunk<
  string,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('invoiceView/isProformaRefrece', async (invoiceId: number, thunkApi) => {
  try {
    return await invoicesApi.isProformaRefrece(invoiceId)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getInvoicePDF = createAsyncThunk<
  string,
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('invoiceView/getInvoicePDF', async (data: string, thunkApi) => {
  try {
    return await invoicesApi.getInvoicePDF(data)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})
const getinvoiceTracker = createAsyncThunk(
  'invoiceView/getinvoiceTracker',
  async (props: invoiceTracker, thunkApi) => {
    try {
      return await invoicesApi.getInvoiceTracker(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialInvoicesState: invoicesListSlice = {
  invoicesList: { CRList: [], milestoneList: [] },
  milestoneList: [],
  isLoading: ApiLoadingState.idle,
  invoicesOfMilestoneList: { listSize: 0, list: [] },
  invoiceSummary: {} as InvoiceSummary,
  isProformaRefrece: '',
  latestestInvoiceVersion: {} as latestInvoiceVersion,
  getinvoiceTracker: {} as invoiceTracker,
  selectedInvoiceID: 0,
}

const invoiceSlice = createSlice({
  name: 'projectView',
  initialState: initialInvoicesState,
  reducers: {
    setSelectedInvoiceID: (state, action) => {
      state.selectedInvoiceID = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClosedMilestonesAndCRs.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.invoicesList = action.payload
      })
      .addCase(getInvoicesOfMilestone.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.invoicesOfMilestoneList = action.payload
      })
      .addCase(getInvoiceSummary.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.invoiceSummary = action.payload
      })
      .addCase(getLatestInvoiceVersionSummary.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.latestestInvoiceVersion = action.payload
      })
      .addCase(isProformaRefrece.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.isProformaRefrece = action.payload
      })
      .addCase(getinvoiceTracker.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getinvoiceTracker = action.payload
      })
      .addMatcher(
        isAnyOf(
          getClosedMilestonesAndCRs.pending,
          getInvoicesOfMilestone.pending,
          getInvoiceSummary.pending,
          getLatestInvoiceVersionSummary.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getClosedMilestonesAndCRs.rejected,
          getInvoicesOfMilestone.rejected,
          getInvoiceSummary.rejected,
          getLatestInvoiceVersionSummary.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.projectInvoices.isLoading

const allMilestoneList = (state: RootState): InvoicesList =>
  state.projectInvoices.invoicesList

const invoicesOfMilestoneList = (state: RootState): InvoicesOfMilestone[] =>
  state.projectInvoices.invoicesOfMilestoneList?.list

const invoicesOfMilestoneSize = (state: RootState): number =>
  state.projectInvoices.invoicesOfMilestoneList.listSize

const invoiceSummary = (state: RootState): InvoiceSummary =>
  state.projectInvoices.invoiceSummary
const latestInvoicevesion = (state: RootState): latestInvoiceVersion =>
  state.projectInvoices.latestestInvoiceVersion

const isProformaRefreceResponse = (state: RootState): string | boolean =>
  state.projectInvoices.isProformaRefrece

const selectedInvoiceID = (state: RootState): number =>
  state.projectInvoices.selectedInvoiceID

const invoicesThunk = {
  getClosedMilestonesAndCRs,
  getInvoicesOfMilestone,
  getInvoiceSummary,
  isProformaRefrece,
  getInvoicePDF,
  getLatestInvoiceVersionSummary,
}

const inVoicesSelectors = {
  isLoading,
  allMilestoneList,
  invoicesOfMilestoneList,
  invoicesOfMilestoneSize,
  invoiceSummary,
  isProformaRefreceResponse,
  latestInvoicevesion,
  getinvoiceTracker,
  selectedInvoiceID,
}

export const invoicesService = {
  ...invoicesThunk,
  actions: invoiceSlice.actions,
  selectors: inVoicesSelectors,
}

export default invoiceSlice.reducer
